import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckIcon, ChevronsUpDownIcon, XIcon } from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAnalyticsParams } from "./useAnalyticsParams";

type Client = components["schemas"]["ClientAdminResponse"];
type AnalyticsOrganisationRow = components["schemas"]["AnalyticsOrganisationRow"];

const MAX_CLIENTS = 5;

const ORG_DISABLED_HINT =
  "Choose at least one client first. Organisations are scoped to the clients selected above.";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function monthToDateIso(): string {
  const date = new Date();
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

interface QuickPick {
  label: string;
  from: () => string;
  to: () => string;
}

const QUICK_PICKS: QuickPick[] = [
  { label: "Last 30 days", from: () => daysAgoIso(29), to: todayIso },
  { label: "Last 90 days", from: () => daysAgoIso(89), to: todayIso },
  { label: "Month to date", from: monthToDateIso, to: todayIso },
];

/** All analytics filters (clients, organisation, window). Sticky: every section reads its scope from here.
 * `showOrganisation={false}` for pages whose endpoints take no organisation filter. */
export function AnalyticsFilterBar({ showOrganisation = true }: { showOrganisation?: boolean } = {}) {
  const { clientIds, from, to, organisationId, setParam } = useAnalyticsParams();
  // Sets from and to in one navigation: two setParam calls would compute from the same params and clobber each other.
  const [, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadClients() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/clients", { params: { query: { Limit: 100 } } });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load clients.");
      } else {
        setClients(data?.data ?? []);
      }
      setLoading(false);
    }
    void loadClients();
    return () => {
      cancelled = true;
    };
  }, []);

  // Scoped to the selected clients and window; clientIds is joined into a stable key for the effect deps.
  const clientIdsKey = clientIds.join(",");
  const [organisations, setOrganisations] = useState<AnalyticsOrganisationRow[]>([]);
  // Starts true when there are clients: the auto-clear effect runs in the same mount flush and can't see the
  // fetch's setOrgsLoading(true), so a false start would clear a valid deep-linked ?org=.
  const [orgsLoading, setOrgsLoading] = useState(clientIds.length > 0);
  const [orgsError, setOrgsError] = useState<string | null>(null);

  useEffect(() => {
    if (clientIds.length === 0 || !showOrganisation) {
      setOrganisations([]);
      setOrgsError(null);
      setOrgsLoading(false);
      return;
    }
    let cancelled = false;
    async function loadOrganisations() {
      setOrgsLoading(true);
      setOrgsError(null);
      const { data, error: apiError } = await api.GET("/admin/analytics/organisations", {
        params: { query: { From: from, To: to, ClientIds: clientIds } },
      });
      if (cancelled) return;
      if (apiError || !data) {
        setOrgsError("Failed to load organisations.");
      } else {
        setOrganisations(data);
      }
      setOrgsLoading(false);
    }
    void loadOrganisations();
    return () => {
      cancelled = true;
    };
    // clientIdsKey stands in for clientIds (same input, stable key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdsKey, from, to, showOrganisation]);

  // Drop a picked organisation that falls out of scope rather than leave an invisible filter.
  useEffect(() => {
    if (!organisationId) return;
    if (clientIds.length === 0) {
      setParam("org", undefined);
      return;
    }
    if (orgsLoading || orgsError) return;
    const stillInScope = organisations.some((org) => org.organisationId === organisationId);
    if (!stillInScope) setParam("org", undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, organisations, orgsLoading, orgsError, clientIds.length]);

  const selectedOrganisation = organisationId
    ? organisations.find((org) => org.organisationId === organisationId)
    : undefined;

  function toggleClient(id: string) {
    if (clientIds.includes(id)) {
      setParam(
        "client",
        clientIds.filter((existing) => existing !== id),
      );
      return;
    }
    if (clientIds.length >= MAX_CLIENTS) return;

    // Picking a client also pulls in its clones (SourceClientId points back), so they appear together.
    const cloneIds = clients
      .filter((client) => client.sourceClientId === id && client.id)
      .map((client) => client.id as string);
    const next = Array.from(new Set([...clientIds, id, ...cloneIds])).slice(0, MAX_CLIENTS);
    setParam("client", next);
  }

  function applyQuickPick(pick: QuickPick) {
    const nextFrom = pick.from();
    const nextTo = pick.to();
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("from", nextFrom);
        next.set("to", nextTo);
        return next;
      },
      { replace: true },
    );
  }

  const selectedClients = clientIds
    .map((id) => clients.find((client) => client.id === id))
    .filter((client): client is Client => Boolean(client));

  // Only clients sharing a name also show the start of their id.
  const duplicateClientNames = new Set(
    clients
      .map((client) => client.name.trim().toLowerCase())
      .filter((name, index, names) => names.indexOf(name) !== index),
  );

  return (
    <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto w-full max-w-[100rem] flex flex-wrap items-center gap-x-6 gap-y-3 px-8 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="justify-between">
                Clients
                <span className="text-muted-foreground text-xs">
                  {clientIds.length}/{MAX_CLIENTS}
                </span>
                <ChevronsUpDownIcon className="text-muted-foreground size-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command>
                <CommandInput placeholder="Search clients..." />
                <CommandList>
                  <CommandEmpty>
                    {loading ? "Loading clients..." : (error ?? "No clients found.")}
                  </CommandEmpty>
                  <CommandGroup>
                    {clients.map((client) => {
                      if (!client.id) return null;
                      const id = client.id;
                      const selected = clientIds.includes(id);
                      const disabled = !selected && clientIds.length >= MAX_CLIENTS;
                      return (
                        <CommandItem
                          key={id}
                          value={`${client.name} ${id}`}
                          disabled={disabled}
                          onSelect={() => toggleClient(id)}
                        >
                          <Checkbox checked={selected} className="mr-1" />
                          <span className="flex-1 truncate">{client.name}</span>
                          {duplicateClientNames.has(client.name.trim().toLowerCase()) && (
                            <span className="text-muted-foreground shrink-0 font-mono text-[10px]">
                              {id.slice(0, 8)}
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {selectedClients.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {selectedClients.map((client) => (
                <Badge key={client.id} variant="secondary" className="gap-1 pr-1">
                  {client.name}
                  <button
                    type="button"
                    aria-label={`Remove ${client.name}`}
                    onClick={() => toggleClient(client.id as string)}
                    className="text-muted-foreground hover:text-foreground rounded-full"
                  >
                    <XIcon className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {showOrganisation && (
        <div className="flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="max-w-56 justify-between"
                disabled={clientIds.length === 0}
              >
                <span className="truncate">
                  {selectedOrganisation ? selectedOrganisation.organisationName : "All organisations"}
                </span>
                <ChevronsUpDownIcon className="text-muted-foreground size-3.5 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command>
                <CommandInput placeholder="Search organisations..." />
                <CommandList>
                  <CommandEmpty>
                    {orgsLoading ? "Loading organisations..." : (orgsError ?? "No organisations found.")}
                  </CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="__all__" onSelect={() => setParam("org", undefined)}>
                      <span className="flex-1">All organisations</span>
                      {!organisationId && <CheckIcon className="size-3.5" />}
                    </CommandItem>
                  </CommandGroup>
                  <CommandGroup>
                    {organisations.map((org) => {
                      const selected = organisationId === org.organisationId;
                      return (
                        <CommandItem
                          key={org.organisationId}
                          value={`${org.organisationName} ${org.clientName} ${org.organisationId}`}
                          onSelect={() => setParam("org", org.organisationId)}
                        >
                          <span className="flex-1 truncate">{org.organisationName}</span>
                          <span className="text-muted-foreground max-w-32 shrink-0 truncate text-[10px]">
                            {org.clientName}
                          </span>
                          {selected && <CheckIcon className="size-3.5" />}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {clientIds.length === 0 && <InfoHint label="Organisation filter">{ORG_DISABLED_HINT}</InfoHint>}
        </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            value={from}
            onChange={(e) => setParam("from", e.target.value)}
            className="h-8 w-36"
            aria-label="From date"
          />
          <span className="text-muted-foreground text-sm">to</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setParam("to", e.target.value)}
            className="h-8 w-36"
            aria-label="To date"
          />
          <div className="flex items-center gap-1">
            {QUICK_PICKS.map((pick) => (
              <Button key={pick.label} type="button" variant="ghost" size="sm" onClick={() => applyQuickPick(pick)}>
                {pick.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
